import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import { Add, Edit, Delete, Search, CheckCircle, Cancel } from "@mui/icons-material";
import { useDiagnosisGroupsAdmin, useUsageCheck } from "../../../stores/MasterTables/therapies.queries";
import { useDeleteDiagnosisGroup } from "../../../stores/MasterTables/therapies.mutations";
import { confirmDeleteWithUsage } from "./confirmDelete";
import ModalDiagnosisGroup from "./ModalDiagnosisGroup";
import type { DiagnosisGroupAdmin } from "./types";
import type { UsePendingChangesReturn } from "./usePendingChanges";
import type { TherapyType } from "../../../api-client";

interface DiagnosisGroupsTabProps {
  pending: UsePendingChangesReturn;
  therapyType?: TherapyType;
}

const DiagnosisGroupsTab: React.FC<DiagnosisGroupsTabProps> = ({ pending, therapyType }) => {
  const { data: groups = [], isLoading } = useDiagnosisGroupsAdmin(therapyType);
  const deleteGroup = useDeleteDiagnosisGroup();
  const checkUsage = useUsageCheck();

  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState<DiagnosisGroupAdmin | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return groups;
    const s = search.toLowerCase();
    return groups.filter(
      (g: DiagnosisGroupAdmin) =>
        g.code?.toLowerCase().includes(s) ||
        g.description?.toLowerCase().includes(s)
    );
  }, [groups, search]);

  const handleCreate = () => {
    setEditItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item: DiagnosisGroupAdmin) => {
    setEditItem(item);
    setModalOpen(true);
  };

  const handleDelete = async (item: DiagnosisGroupAdmin) => {
    const usage = await checkUsage("diagnosisGroup", item.id);
    if (!confirmDeleteWithUsage(`Diagnosegruppe ${item.code}`, usage)) return;
    deleteGroup.mutate(item.id);
  };

  if (isLoading) {
    return (
      <Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <TextField
          placeholder="Suchen…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate} size="small">
          Neue Diagnosegruppe
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Code</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>Beschreibung</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">a</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">b</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">c</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">d</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Relevant</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Primär</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Sekundär</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }}>RxType</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#000" }} align="center">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {search ? "Keine Treffer" : "Keine Diagnosegruppen vorhanden"}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((g: DiagnosisGroupAdmin) => (
                <TableRow
                  key={g.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleEdit(g)}
                >
                  <TableCell>
                    <Chip label={g.code} size="small" sx={{ fontWeight: 600, color: "#000", borderColor: "#333" }} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ color: "#000" }}>{g.description}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#000" }}>
                      {g.a ?? "–"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#000" }}>
                      {g.b ?? "–"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#000" }}>
                      {g.c ?? "–"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontFamily: "monospace", color: "#000" }}>
                      {g.d ?? "–"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {g.isRelevant ? (
                      <CheckCircle fontSize="small" sx={{ color: "success.main" }} />
                    ) : (
                      <Cancel fontSize="small" sx={{ color: "text.disabled" }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ color: "#000" }}>{g.PrimaryTreatments ?? "–"}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ color: "#000" }}>{g.SecondaryTreatments ?? "–"}</Typography>
                  </TableCell>
                  <TableCell sx={{ color: "#000" }}>{g.RxType?.id || "–"}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Bearbeiten">
                      <IconButton size="small" onClick={() => handleEdit(g)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton size="small" color="error" onClick={() => handleDelete(g)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        {filtered.length} von {groups.length} Diagnosegruppen
      </Typography>

      {/* Modal */}
      <ModalDiagnosisGroup
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        editItem={editItem}
      />
    </Box>
  );
};

export default DiagnosisGroupsTab;
